import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate, useLocation, Link } from 'react-router-dom';
import {
  IconChevronDown,
  IconHome2,
  IconBox,
  IconReceipt2,
  IconWallet,
  IconHistory,
  IconPhoto,
  IconDiamond,
  IconChartBar,
  IconX,
  IconLogout,
  IconSun,
  IconMoon,
  IconBuilding,
  IconUsers,
  IconFileText,
  IconTruckDelivery,
  IconSettings,
  IconClipboardCheck,
  IconShoppingCart,
  IconShoppingCartOff,
  IconBell,
} from '@tabler/icons-react';
import AddJenisBarang from '../components/Admin/AddJenisBarang';
import ConfirmationLogOut from '../components/ConfirmationLogOut';
import ModalLogout from './ModalLogout';
import { http, getAuthorizationHeader } from '../config/http';
import { useTheme } from '../context/ThemeContext';
import { useNotification } from '../context/NotificationContext';
import { useRefundAccess } from '../hooks/useRefundAccess';
import logoKD from '../assets/logo_kd.png';

function Sidebar({ sidebarOpen, setSidebarOpen }) {
  const { pendingCounts } = useNotification();
  const { isRefundAllowed } = useRefundAccess();
  const location = useLocation();
  const isPathActive = (prefixes) => prefixes.some((p) => location.pathname.includes(p));

  const [openSubMenuBarang, setOpenSubMenuBarang] = useState(() =>
    isPathActive(['/list-items-admin', '/list-items-admin-v2', '/list-barang-v2', '/barang-terhapus', '/barang-terhapus-v2', '/verifikasi-transfer-toko', '/verifikasi-transfer-toko-v2', '/histori-penghapusan', '/barang-opname', '/barang-rusak'])
  );
  const [openSubMenuUser, setOpenSubMenuUser] = useState(() =>
    location.pathname.startsWith('/list-users') || location.pathname.startsWith('/karyawan')
  );
  const [openSubMenuTransaksi, setOpenSubMenuTransaksi] = useState(() =>
    isPathActive([
      '/transaksi-penjualan',
      '/transaksi-penjualan-v2',
      '/transaksi-pembelian',
      '/transaksi-pembelian-v2',
      '/penjualan',
      '/pengembalian',
      '/pembelian',
      '/data-pembelian',
      '/list-transaksi-tertunda',
      '/transaksi-tertunda',
      '/laporan-pembayaran',
      '/summary'
    ])
  );
  const [openSubMenuCashflow, setOpenSubMenuCashflow] = useState(() =>
    isPathActive([
      '/saldo-kas',
      '/history-saldo-kasir',
      '/pengeluaran',
      '/trx-luar',
      '/laporan-cashflow'
    ])
  );
  const [openSubMenuHistori, setOpenSubMenuHistori] = useState(() =>
    isPathActive([
      '/histori-transfer-masuk',
      '/histori-masuk-keluar',
      '/histori-transfer-keluar',
      '/riwayat',
      '/verifikasi-transfer-toko',
      '/verifikasi-transfer-toko-v2'
    ])
  );
  const [openSubMenuGaleri, setOpenSubMenuGaleri] = useState(() =>
    isPathActive(['/galeri-barang', '/galeri-penjualan', '/galeri-pembelian'])
  );
  const [openSubMenuBrankas, setOpenSubMenuBrankas] = useState(() =>
    isPathActive([
      '/list-brankas',
      '/permohonan-pindah-brankas',
      '/histori-permohonan-brankas',
      '/brankas/histori-hapus',
      '/laporan-keluar-masuk-operator'
    ])
  );
  const [openSubMenuEtalase, setOpenSubMenuEtalase] = useState(() =>
    isPathActive(['/list-etalase', '/etalase-keluar'])
  );
  const [openSubMenuSuratJalan, setOpenSubMenuSuratJalan] = useState(() =>
    isPathActive(['/buat-surat-jalan', '/histori-pengiriman'])
  );
  const [openSubMenuDashboardReport, setOpenSubMenuDashboardReport] = useState(() =>
    isPathActive(['/laba-penjualan', '/dashboard-keuangan', '/analitik-emas'])
  );
  const [openSubMenuVerifikasi, setOpenSubMenuVerifikasi] = useState(() =>
    isPathActive([
      '/verifikasi-permohonan-brankas',
      '/verifikasi-permohonan-brankas-v2',
      '/verifikasi-hapus-etalase',
      '/verifikasi-hapus-etalase-v2',
      '/verifikasi-hapus-pembelian',
      '/verifikasi-transfer-toko',
      '/verifikasi-transfer-toko-v2',
      '/histori-penghapusan'
    ])
  );
  let [isOpen, setIsOpen] = useState(false);

  const toggleSubMenu = (menuName) => {
    setOpenSubMenuBarang(menuName === 'barang' ? !openSubMenuBarang : false);
    setOpenSubMenuUser(menuName === 'user' ? !openSubMenuUser : false);
    setOpenSubMenuTransaksi(menuName === 'transaksi' ? !openSubMenuTransaksi : false);
    setOpenSubMenuCashflow(menuName === 'cashflow' ? !openSubMenuCashflow : false);
    setOpenSubMenuHistori(menuName === 'histori' ? !openSubMenuHistori : false);
    setOpenSubMenuGaleri(menuName === 'galeri' ? !openSubMenuGaleri : false);
    setOpenSubMenuBrankas(menuName === 'brankas' ? !openSubMenuBrankas : false);
    setOpenSubMenuEtalase(menuName === 'etalase' ? !openSubMenuEtalase : false);
    setOpenSubMenuSuratJalan(menuName === 'suratjalan' ? !openSubMenuSuratJalan : false);
    setOpenSubMenuDashboardReport(menuName === 'dashboardreport' ? !openSubMenuDashboardReport : false);
    setOpenSubMenuVerifikasi(menuName === 'verifikasi' ? !openSubMenuVerifikasi : false);
  };

  const closeSidebarMobile = () => {
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  };

  const trigger = useRef(null);
  const sidebar = useRef(null);
  const { theme, toggleTheme } = useTheme();

  const storedSidebarExpanded = localStorage.getItem('sidebar-expanded');
  const [sidebarExpanded, setSidebarExpanded] = useState(
    storedSidebarExpanded === null ? false : storedSidebarExpanded === 'true'
  );

  const [isOpenModalLogout, setIsOpenModalLogout] = useState(false);

  function closeModalLogout() {
    setIsOpenModalLogout(false);
  }

  // close on click outside
  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!sidebar.current || !trigger.current) return;
      if (
        !sidebarOpen ||
        sidebar.current.contains(target) ||
        trigger.current.contains(target) ||
        (target.closest && (target.closest('#sidebar') || target.closest('[aria-controls="sidebar"]')))
      ) {
        return;
      }
      // Do not auto-close on desktop clicks outside the boundary, as the toggle button controls it.
      if (window.innerWidth >= 1024) return;
      setSidebarOpen(false);
    };
    document.addEventListener('click', clickHandler);
    return () => document.removeEventListener('click', clickHandler);
  }, [sidebarOpen, setSidebarOpen]);

  // close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!sidebarOpen || keyCode !== 27) return;
      setSidebarOpen(false);
    };
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  }, [sidebarOpen, setSidebarOpen]);

  useEffect(() => {
    localStorage.setItem('sidebar-expanded', sidebarExpanded);
    if (sidebarExpanded) {
      document.querySelector('body').classList.add('sidebar-expanded');
    } else {
      document.querySelector('body').classList.remove('sidebar-expanded');
    }
  }, [sidebarExpanded]);

  // Auto-expand submenus based on active routes so parent menu dropdown stays open when focused/navigated
  useEffect(() => {
    const path = location.pathname;

    // Verifikasi
    if (
      path.includes('/verifikasi-permohonan-brankas') ||
      path.includes('/verifikasi-hapus-etalase') ||
      path.includes('/verifikasi-hapus-pembelian') ||
      path.includes('/verifikasi-transfer-toko')
    ) {
      setOpenSubMenuVerifikasi(true);
    }

    // Barang
    if (path.includes('/list-items-admin') || path.includes('/list-barang-v2') || path.includes('/barang-terhapus') || path.includes('/verifikasi-transfer-toko')) {
      setOpenSubMenuBarang(true);
    }
    if (location.pathname.startsWith('/list-users') || location.pathname.startsWith('/karyawan')) {
      setOpenSubMenuUser(true);
    }

    // Brankas
    if (
      path.includes('/list-brankas') ||
      path.includes('/permohonan-pindah-brankas') ||
      path.includes('/histori-permohonan-brankas')
    ) {
      setOpenSubMenuBrankas(true);
    }

    // Surat Jalan
    if (path.includes('/buat-surat-jalan') || path.includes('/histori-pengiriman')) {
      setOpenSubMenuSuratJalan(true);
    }

    // Etalase
    if (path.includes('/list-etalase') || path.includes('/etalase-keluar')) {
      setOpenSubMenuEtalase(true);
    }

    // Transaksi
    if (
      path.includes('/transaksi-penjualan') ||
      path.includes('/transaksi-penjualan-v2') ||
      path.includes('/transaksi-pembelian') ||
      path.includes('/transaksi-pembelian-v2') ||
      path.includes('/penjualan') ||
      path.includes('/pengembalian') ||
      path.includes('/list-transaksi-tertunda') ||
      path.includes('/transaksi-tertunda') ||
      path.includes('/laporan-pembayaran') ||
      path.includes('/summary')
    ) {
      setOpenSubMenuTransaksi(true);
    }

    // Cashflow
    if (
      path.includes('/saldo-kas') ||
      path.includes('/history-saldo-kasir') ||
      path.includes('/pengeluaran') ||
      path.includes('/trx-luar') ||
      path.includes('/laporan-cashflow')
    ) {
      setOpenSubMenuCashflow(true);
    }

    // Histori Transfer Cabang
    if (path.includes('/histori-transfer-masuk') || path.includes('/histori-masuk-keluar') || path.includes('/riwayat') || path.includes('/verifikasi-transfer-toko')) {
      setOpenSubMenuHistori(true);
    }

    // Galeri
    if (path.includes('/galeri-barang') || path.includes('/galeri-penjualan') || path.includes('/galeri-pembelian')) {
      setOpenSubMenuGaleri(true);
    }
  }, [location.pathname]);

  function closeModal() {
    setIsOpen(false);
  }
  function openModal() {
    setIsOpen(true);
  }

  const navigate = useNavigate();

  async function logOut() {
    try {
      await http.post('/auth/logout', {}, {
        headers: { Authorization: getAuthorizationHeader() }
      });
    } catch (err) {
      console.error('Logout error:', err);
    }
    localStorage.removeItem('role');
    localStorage.removeItem('token');
    localStorage.removeItem('toko');
    localStorage.removeItem('name');
    localStorage.removeItem('tipe_surat');
    sessionStorage.removeItem('brankasPinVerified');
    window.dispatchEvent(new Event('logout-success'));
    navigate('/login');
  }

  const user = localStorage.getItem('role');
  const toko = localStorage.getItem('toko');

  // Nav items and child menus must NEVER trigger hide or close sidebar
  const handleNavClick = (e) => {
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }
    closeSidebarMobile();
  };

  // Stylish link component
  const NavItem = ({ to, icon: Icon, label }) => (
    <NavLink
      to={to}
      onClick={handleNavClick}
      className={({ isActive }) =>
        `flex flex-row justify-start items-center gap-3 px-4 py-2 text-sm rounded-xl mb-0.5 cursor-pointer transition-all duration-200 group ${isActive
          ? 'bg-amber-800/10 dark:bg-amber-500/15 text-amber-900 dark:text-amber-400 font-bold shadow-sm border border-amber-800/20 dark:border-amber-500/30'
          : 'text-slate-800 font-medium dark:text-slate-300 hover:bg-amber-800/5 dark:hover:bg-slate-800/80 hover:text-amber-900 dark:hover:text-amber-400'
        }`
      }
    >
      {({ isActive }) => (
        <>
          <Icon
            className={`w-[20px] h-[20px] transition-colors ${isActive ? 'text-amber-800 dark:text-amber-400' : 'text-slate-600 dark:text-slate-400 group-hover:text-amber-900 dark:group-hover:text-amber-400'
              }`}
            stroke={isActive ? 2.5 : 1.75}
          />
          <span className="truncate">{label}</span>
        </>
      )}
    </NavLink>
  );

  const SubMenuItem = ({ to, label, onClick, badge }) => {
    if (onClick) {
      return (
        <div
          onClick={(e) => {
            e.stopPropagation();
            closeSidebarMobile();
            onClick(e);
          }}
          className="py-1.5 text-sm text-slate-700 font-medium dark:text-slate-300 hover:text-amber-900 dark:hover:text-amber-400 cursor-pointer transition-colors flex items-center justify-between gap-2 rounded-xl px-4 hover:bg-amber-800/5 dark:hover:bg-slate-800/80"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-1.5 h-1.5 rounded-full bg-slate-400 dark:bg-slate-500 transition-colors" />
            <span className="truncate">{label}</span>
          </div>
          {Boolean(badge && badge > 0) && (
            <span className="shrink-0 px-1.5 py-0.5 text-[10px] font-bold text-white bg-rose-500 rounded-full min-w-[18px] text-center shadow-xs">
              {badge > 99 ? '99+' : badge}
            </span>
          )}
        </div>
      );
    }
    return (
      <NavLink
        to={to}
        onClick={handleNavClick}
        className={({ isActive }) =>
          `py-1.5 text-sm cursor-pointer transition-all flex items-center justify-between gap-2 rounded-xl px-4 hover:bg-amber-800/5 dark:hover:bg-slate-800/80 group ${isActive 
            ? 'text-amber-900 dark:text-amber-400 font-bold bg-amber-800/10 dark:bg-slate-800/90 shadow-sm' 
            : 'text-slate-700 font-medium dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100'
          }`
        }
      >
        {({ isActive }) => (
          <>
            <div className="flex items-center gap-3 min-w-0">
              <div
                className={`w-1.5 h-1.5 rounded-full transition-colors shrink-0 ${isActive ? 'bg-amber-800 dark:bg-amber-400 shadow-[0_0_6px_rgba(245,158,11,0.6)]' : 'bg-slate-400 dark:bg-slate-600 group-hover:bg-amber-700'
                  }`}
              />
              <span className="truncate">{label}</span>
            </div>
            {Boolean(badge && badge > 0) && (
              <span className="shrink-0 px-1.5 py-0.5 text-[10px] font-bold text-white bg-rose-500 rounded-full min-w-[18px] text-center shadow-xs">
                {badge > 99 ? '99+' : badge}
              </span>
            )}
          </>
        )}
      </NavLink>
    );
  };

  const ParentMenu = ({ icon: Icon, label, isOpen: menuOpen, onClick, badge }) => (
    <div
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className={`flex flex-row justify-between items-center gap-3 px-4 py-2 text-sm rounded-xl mb-0.5 cursor-pointer transition-all duration-200 group ${menuOpen
        ? 'bg-amber-800/10 dark:bg-slate-800/80 text-amber-900 dark:text-amber-400 font-bold'
        : 'text-slate-800 font-medium dark:text-slate-300 hover:bg-amber-800/5 dark:hover:bg-slate-800/80 hover:text-amber-900 dark:hover:text-amber-400'
        }`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <Icon
          className={`w-[20px] h-[20px] shrink-0 transition-colors ${menuOpen ? 'text-amber-800 dark:text-amber-400' : 'text-slate-600 dark:text-slate-400 group-hover:text-amber-900 dark:group-hover:text-amber-400'
            }`}
          stroke={menuOpen ? 2.2 : 1.75}
        />
        <span className={`${menuOpen ? 'font-bold' : 'font-medium'} truncate`}>{label}</span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {Boolean(badge && badge > 0) && (
          <span className="relative flex items-center justify-center">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75"></span>
            <span className="relative inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white bg-rose-500 rounded-full shadow-sm min-w-[18px]">
              <IconBell size={11} stroke={2.5} className="animate-bounce" />
              <span>{badge > 99 ? '99+' : badge}</span>
            </span>
          </span>
        )}
        <IconChevronDown
          className={`w-4 h-4 transition-transform duration-200 ${menuOpen ? 'rotate-180 text-amber-800 dark:text-amber-400' : 'text-slate-400 dark:text-slate-500 group-hover:text-amber-700 dark:group-hover:text-amber-400'
            }`}
        />
      </div>
    </div>
  );

  return (
    <div>
      <ModalLogout isOpenModalLogout={isOpenModalLogout} closeModaLogout={closeModalLogout} logout={logOut} />
      <AddJenisBarang isOpen={isOpen} closeModal={closeModal} />
      <ConfirmationLogOut />

      {/* Mobile Backdrop */}
      <div
        className={`fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40 lg:hidden transition-opacity duration-300 ${sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        aria-hidden="true"
        onClick={() => setSidebarOpen(false)}
      ></div>

      {/* Sidebar Content */}
      <div
        id="sidebar"
        ref={sidebar}
        className={`flex flex-col w-[17rem] absolute outline-none z-50 left-0 top-0 lg:static lg:left-auto lg:top-auto h-screen shrink-0 bg-gradient-to-b from-yellow-100 via-amber-100/70 to-yellow-200/60 dark:from-slate-900 dark:via-slate-900 dark:to-slate-950 border-r border-amber-300/60 dark:border-slate-800 shadow-[4px_0_24px_-10px_rgba(0,0,0,0.05)] dark:shadow-none transition-all duration-300 ease-in-out ${sidebarOpen ? 'translate-x-0 lg:ml-0' : '-translate-x-full lg:-ml-[17rem]'
          }`}
      >
        {/* Sidebar header */}
        <div className="flex items-center justify-between px-6 py-3 border-b border-amber-200/60 dark:border-slate-800">
          <Link to="/" onClick={closeSidebarMobile} className="flex items-center gap-2.5 group outline-none">
            <div className="w-9 h-9 rounded-full overflow-hidden border-2 border-amber-300/60 dark:border-amber-500/40 bg-amber-50 dark:bg-slate-800 shadow-sm shadow-amber-200/50 dark:shadow-amber-900/30 flex-shrink-0 p-0.5">
              <img src={logoKD} alt="Komala Dewi" className="w-full h-full object-contain" />
            </div>
            <div className="flex flex-col">
              <span className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-amber-600 to-amber-800 dark:from-amber-400 dark:to-amber-600 tracking-tight leading-tight">
                Komala Dewi
              </span>
              {toko && (
                <span className="text-xs font-semibold text-amber-600/80 dark:text-amber-400/70 uppercase tracking-wider leading-tight">
                  {toko}
                </span>
              )}
            </div>
          </Link>

          {/* Close button (Mobile) */}
          <button
            ref={trigger}
            className="lg:hidden text-slate-400 dark:text-slate-500 hover:text-amber-600 dark:hover:text-amber-400 hover:bg-amber-50 dark:hover:bg-slate-800 p-1.5 rounded-lg transition-colors outline-none"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-controls="sidebar"
            aria-expanded={sidebarOpen}
          >
            <span className="sr-only">Close sidebar</span>
            <IconX size={20} />
          </button>
        </div>

        {/* Links Container */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 custom-scrollbar">
          <div className="space-y-1">
            <NavItem to="/" icon={IconHome2} label="Dashboard" />

            {/* Admin & Owner & Verifikator menu */}
            {(user === 'owner' || user === 'admin' || user === 'verifikator') && (
              <>
                {user === 'owner' && <NavItem to="/owner/cabang" icon={IconBuilding} label="Manajemen Cabang" />}
                {/* Manajemen User dipindah ke bawah */}

                {/* Verifikasi Menu */}
                <div>
                  <ParentMenu
                    icon={IconClipboardCheck}
                    label="Verifikasi"
                    badge={pendingCounts.total}
                    isOpen={openSubMenuVerifikasi}
                    onClick={() => toggleSubMenu('verifikasi')}
                  />
                  <div
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${openSubMenuVerifikasi ? 'max-h-96 opacity-100 mb-2' : 'max-h-0 opacity-0'
                      }`}
                  >
                    <div className="flex flex-col gap-0.5 pl-8 pr-2 pt-1 pb-2 relative">
                      <div className="absolute left-[29px] top-2 bottom-4 w-px bg-slate-100 dark:bg-slate-700" />
                      <SubMenuItem to="/verifikasi-transfer-toko" label="Transfer Toko" badge={pendingCounts.transfer_toko} />
                      <SubMenuItem to="/verifikasi-transfer-toko-v2" label="Transfer Toko V2" badge={pendingCounts.transfer_toko} />
                      <SubMenuItem to="/verifikasi-permohonan-brankas" label="Brankas Pusat" badge={pendingCounts.permohonan_brankas} />
                      <SubMenuItem to="/verifikasi-hapus-etalase" label="Hapus Etalase" badge={pendingCounts.hapus_etalase} />
                      <SubMenuItem to="/verifikasi-permohonan-brankas-v2" label="Brankas Pusat V2" badge={pendingCounts.permohonan_brankas} />
                      <SubMenuItem to="/verifikasi-hapus-etalase-v2" label="Hapus Etalase V2" badge={pendingCounts.hapus_etalase} />
                    </div>
                  </div>
                </div>
              </>
            )}


            {/* Kasir Penjualan Menu */}
            {user === 'kasir' && (
              <>
                <NavItem to="/penjualan" icon={IconShoppingCart} label="Kasir Penjualan" />
                {isRefundAllowed && (
                  <NavItem to="/pengembalian" icon={IconShoppingCartOff} label="Pengembalian" />
                )}
                <NavItem to="/transaksi-penjualan" icon={IconReceipt2} label="List Penjualan" />
                <NavItem to="/transaksi-penjualan-v2" icon={IconReceipt2} label="List Penjualan V2" />
              </>
            )}

            {/* Kasir Pembelian Menu */}
            {user === 'kasir_pembelian' && (
              <>
                <NavItem to="/pembelian" icon={IconShoppingCart} label="Kasir Pembelian" />
                {isRefundAllowed && (
                  <NavItem to="/pengembalian" icon={IconShoppingCartOff} label="Pengembalian" />
                )}
                <NavItem to="/transaksi-pembelian" icon={IconReceipt2} label="List Pembelian" />
                <NavItem to="/transaksi-pembelian-v2" icon={IconReceipt2} label="List Pembelian V2" />
              </>
            )}

            {/* Barang Menu (Operator, Owner, Admin, Verifikator) */}
            {(user === 'owner' || (user !== 'owner' && user !== 'kasir' && user !== 'kasir_pembelian')) && user !== 'operator_rumah' && user !== 'supervisor' && (
              <div>
                <ParentMenu
                  icon={IconBox}
                  label="Barang"
                  isOpen={openSubMenuBarang}
                  onClick={() => toggleSubMenu('barang')}
                />
                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${openSubMenuBarang ? 'max-h-96 opacity-100 mb-2' : 'max-h-0 opacity-0'
                    }`}
                >
                  <div className="flex flex-col gap-0.5 pl-8 pr-2 pt-1 pb-2 relative">
                    <div className="absolute left-[29px] top-2 bottom-4 w-px bg-slate-100 dark:bg-slate-700" />
                    <SubMenuItem onClick={openModal} label="Jenis Barang" />
                    <SubMenuItem to="/list-items-admin" label="List Barang" />
                    {(user === 'operator' || user === 'operator_toko') && (
                      <>
                        <SubMenuItem to="/histori-penghapusan" label="Pengajuan Hapus" badge={pendingCounts.hapus_etalase} />
                        <SubMenuItem to="/verifikasi-transfer-toko" label="Transfer Toko" badge={pendingCounts.transfer_toko} />
                        <SubMenuItem to="/verifikasi-transfer-toko-v2" label="Transfer Toko V2" badge={pendingCounts.transfer_toko} />
                      </>
                    )}
                    <SubMenuItem to="/list-items-admin-v2" label="List Barang V2" />
                    <SubMenuItem to="/barang-terhapus" label="Barang Dihapus V1" />
                    <SubMenuItem to="/barang-terhapus-v2" label="Barang Dihapus V2" />
                  </div>
                </div>
              </div>
            )}

            {/* Core Operational Menus (Exclude Kasir only) */}
            {user !== 'kasir' && user !== 'kasir_pembelian' && (<>

              {/* Brankas Menu */}
              {user !== 'operator' && (
                <div>
                  <ParentMenu
                    icon={IconBox}
                    label="Brankas"
                    isOpen={openSubMenuBrankas}
                    onClick={() => toggleSubMenu('brankas')}
                  />
                  <div
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${openSubMenuBrankas ? 'max-h-80 opacity-100 mb-2' : 'max-h-0 opacity-0'
                      }`}
                  >
                    <div className="flex flex-col gap-0.5 pl-8 pr-2 pt-1 pb-2 relative">
                      <div className="absolute left-[29px] top-2 bottom-4 w-px bg-slate-100 dark:bg-slate-700" />
                      <SubMenuItem to="/list-brankas" label="List Brankas" />
                      {(user === 'operator_rumah' || user === 'owner' || user === 'admin' || user === 'supervisor') && (
                        <SubMenuItem to="/laporan-keluar-masuk-operator" label="Keluar - Masuk" />
                      )}
                      {user === 'operator_rumah' && (
                        <SubMenuItem to="/permohonan-pindah-brankas" label="Pindah Ke Pusat" />
                      )}
                      {user !== 'supervisor' && (
                        <>
                          <SubMenuItem to="/histori-permohonan-brankas" label="Histori Pindah Pusat" />
                          <SubMenuItem to="/brankas/histori-hapus" label="Histori Hapus" />
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Surat Jalan / Pengiriman (V5 Core Feature) */}
              {(user === 'operator' || user === 'kepala_toko' || user === 'admin' || user === 'owner' || user === 'supervisor') && (
                <div>
                  <ParentMenu
                    icon={IconTruckDelivery}
                    label="Pengiriman"
                    isOpen={openSubMenuSuratJalan}
                    onClick={() => toggleSubMenu('suratjalan')}
                  />
                  <div
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${openSubMenuSuratJalan ? 'max-h-80 opacity-100 mb-2' : 'max-h-0 opacity-0'
                      }`}
                  >
                    <div className="flex flex-col gap-0.5 pl-8 pr-2 pt-1 pb-2 relative">
                      <div className="absolute left-[29px] top-2 bottom-4 w-px bg-slate-100 dark:bg-slate-700" />
                      {(user === 'owner' || user === 'admin' || user === 'kepala_toko' || user === 'supervisor') && (
                        <SubMenuItem to="/buat-surat-jalan" label="Buat Pengiriman" />
                      )}
                      <SubMenuItem to="/histori-pengiriman" label="Daftar & Validasi" />
                    </div>
                  </div>
                </div>
              )}



              {/* Etalase Menu */}
              {/* {user !== 'operator_rumah' && (
                <div>
                  <ParentMenu
                    icon={IconBox}
                    label="Etalase"
                    isOpen={openSubMenuEtalase}
                    onClick={() => toggleSubMenu('etalase')}
                  />
                  <div
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${openSubMenuEtalase ? 'max-h-80 opacity-100 mb-2' : 'max-h-0 opacity-0'
                      }`}
                  >
                    <div className="flex flex-col gap-0.5 pl-8 pr-2 pt-1 pb-2 relative">
                      <div className="absolute left-[29px] top-2 bottom-4 w-px bg-slate-100 dark:bg-slate-700" />
                      <SubMenuItem to="/list-etalase" label="List Etalase" />
                      <SubMenuItem to="/etalase-keluar" label="Keluar" />
                      <SubMenuItem to="/histori-transfer-masuk" label="Masuk" />
                    </div>
                  </div>
                </div>
              )} */}

              {/* Scan Kwitansi */}
              {user !== 'operator_rumah' && user !== 'supervisor' && (
                <NavItem to="/scan-kwitansi" icon={IconFileText} label="Scan Kwitansi" />
              )}

              {/* Transaksi Menu */}
              {user !== 'operator_rumah' && user !== 'supervisor' && (
                <div>
                  <ParentMenu
                    icon={IconReceipt2}
                    label="Transaksi"
                    isOpen={openSubMenuTransaksi}
                    onClick={() => toggleSubMenu('transaksi')}
                  />
                  <div
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${openSubMenuTransaksi ? 'max-h-80 opacity-100 mb-2' : 'max-h-0 opacity-0'
                      }`}
                  >
                    <div className="flex flex-col gap-0.5 pl-8 pr-2 pt-1 pb-2 relative">
                      <div className="absolute left-[29px] top-2 bottom-4 w-px bg-slate-100 dark:bg-slate-700" />
                      <SubMenuItem to="/transaksi-penjualan" label="Penjualan" />
                      <SubMenuItem to="/transaksi-penjualan-v2" label="Penjualan V2" />
                      <SubMenuItem to="/transaksi-pembelian" label="Pembelian" />
                      <SubMenuItem to="/transaksi-pembelian-v2" label="Pembelian V2" />
                      <SubMenuItem to="/list-transaksi-tertunda" label="Penggarus" />
                      <SubMenuItem to="/laporan-pembayaran" label="Pembayaran" />
                      <SubMenuItem to="/summary" label="Laporan" />
                    </div>
                  </div>
                </div>
              )}

              {/* Cashflow Menu */}
              {user !== 'operator_rumah' && user !== 'supervisor' && (
                <div>
                  <ParentMenu
                    icon={IconWallet}
                    label="Cashflow"
                    isOpen={openSubMenuCashflow}
                    onClick={() => toggleSubMenu('cashflow')}
                  />
                  <div
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${openSubMenuCashflow ? 'max-h-80 opacity-100 mb-2' : 'max-h-0 opacity-0'
                      }`}
                  >
                    <div className="flex flex-col gap-0.5 pl-8 pr-2 pt-1 pb-2 relative">
                      <div className="absolute left-[29px] top-2 bottom-4 w-px bg-slate-100 dark:bg-slate-700" />
                      <SubMenuItem to="/saldo-kas" label="Saldo KAS" />
                      <SubMenuItem to="/pengeluaran" label="Transaksi Luar" />
                      <SubMenuItem to="/laporan-cashflow" label="Laporan" />
                    </div>
                  </div>
                </div>
              )}

              {/* Riwayat Logic */}
              {user !== 'operator_rumah' && user !== 'supervisor' && (user === 'admin' ? (
                <NavItem to="/riwayat" icon={IconHistory} label="Transfer Cabang" />
              ) : (
                <div>
                  <ParentMenu
                    icon={IconHistory}
                    label="Transfer Cabang"
                    isOpen={openSubMenuHistori}
                    onClick={() => toggleSubMenu('histori')}
                  />
                  <div
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${openSubMenuHistori ? 'max-h-80 opacity-100 mb-2' : 'max-h-0 opacity-0'
                      }`}
                  >
                    <div className="flex flex-col gap-0.5 pl-8 pr-2 pt-1 pb-2 relative">
                      <div className="absolute left-[29px] top-2 bottom-4 w-px bg-slate-100 dark:bg-slate-700" />
                      <SubMenuItem to="/histori-transfer-masuk" label="Barang Masuk" />
                      <SubMenuItem to="/histori-masuk-keluar" label="Barang Keluar" />
                    </div>
                  </div>
                </div>
              ))}

              {/* Galeri Menu */}
              {user !== 'supervisor' && (
                <div>
                  <ParentMenu
                    icon={IconPhoto}
                    label="Galeri"
                    isOpen={openSubMenuGaleri}
                    onClick={() => toggleSubMenu('galeri')}
                  />
                  <div
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${openSubMenuGaleri ? 'max-h-80 opacity-100 mb-2' : 'max-h-0 opacity-0'
                      }`}
                  >
                    <div className="flex flex-col gap-0.5 pl-8 pr-2 pt-1 pb-2 relative">
                      <div className="absolute left-[29px] top-2 bottom-4 w-px bg-slate-100 dark:bg-slate-700" />
                      {user !== 'operator_rumah' && (
                        <>
                          <SubMenuItem to="/galeri-barang" label="Barang" />
                          <SubMenuItem to="/galeri-penjualan" label="Penjualan" />
                        </>
                      )}
                      <SubMenuItem to="/galeri-pembelian" label="Pembelian" />
                    </div>
                  </div>
                </div>
              )}

              {/* Role-specific items */}
              {user !== 'admin' && user !== 'operator' && user !== 'supervisor' && (
                <NavItem to="/tambah-barang" icon={IconDiamond} label="Tambah Barang" />
              )}

            </>)} {/* end non-owner */}

            {user === 'admin' && (
              <div>
                <ParentMenu
                  icon={IconChartBar}
                  label="Dashboard Report"
                  isOpen={openSubMenuDashboardReport}
                  onClick={() => toggleSubMenu('dashboardreport')}
                />
                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${openSubMenuDashboardReport ? 'max-h-80 opacity-100 mb-2' : 'max-h-0 opacity-0'
                    }`}
                >
                  <div className="flex flex-col gap-0.5 pl-8 pr-2 pt-1 pb-2 relative">
                    <div className="absolute left-[29px] top-2 bottom-4 w-px bg-slate-100 dark:bg-slate-700" />
                    <SubMenuItem to="/laba-penjualan" label="Laba Penjualan" />
                    <SubMenuItem to="/dashboard-keuangan" label="Dashboard Keuangan" />
                    <SubMenuItem to="/analitik-emas" label="Analitik Emas" />
                  </div>
                </div>
                <NavItem to="/pengaturan" icon={IconSettings} label="Pengaturan Sistem" />
              </div>
            )}

            {/* {user === 'admin' && (
              <NavItem to="/riwayat-stok-harian" icon={IconHistory} label="Riwayat Stok" />
            )} */}

            {(user === 'owner' || user === 'admin') && (
              <div>
                <ParentMenu
                  icon={IconUsers}
                  label="Manajemen User"
                  isOpen={openSubMenuUser}
                  onClick={() => toggleSubMenu('user')}
                />
                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${openSubMenuUser ? 'max-h-80 opacity-100 mb-2' : 'max-h-0 opacity-0'
                    }`}
                >
                  <div className="flex flex-col gap-0.5 pl-8 pr-2 pt-1 pb-2 relative">
                    <div className="absolute left-[29px] top-2 bottom-4 w-px bg-slate-100 dark:bg-slate-700" />
                    <SubMenuItem to="/list-users" label="Akun Kasir / User" />
                    <SubMenuItem to="/karyawan" label="Data Karyawan" />
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>

        {/* User Profile / Logout footer */}
        <div className="p-4 border-t border-amber-200/60 dark:border-slate-800 bg-slate-50/30 dark:bg-slate-900/60">
          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className="w-full mb-3 py-2 px-3 rounded-xl flex justify-center items-center gap-2 text-sm font-medium transition-all duration-300 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:border-amber-300 dark:hover:border-amber-600 hover:text-amber-600 dark:hover:text-amber-400 hover:shadow-sm"
          >
            {theme === 'light' ? (
              <>
                <IconMoon size={18} className="text-amber-500" />
                <span>Dark Mode</span>
              </>
            ) : (
              <>
                <IconSun size={18} className="text-amber-400" />
                <span>Light Mode</span>
              </>
            )}
          </button>

          <div className="bg-white dark:bg-slate-800 rounded-xl p-3 shadow-sm border border-amber-100/40 dark:border-slate-700/60 flex flex-col gap-3">
            <div className="flex items-center gap-3 px-1">
              <div className="w-10 h-10 rounded-full bg-amber-100/50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 flex items-center justify-center font-bold shadow-inner border border-amber-200/60 dark:border-amber-700/40 shrink-0">
                {user ? user.charAt(0).toUpperCase() : 'U'}
              </div>
              <div className="flex flex-col overflow-hidden">
                <span className="text-sm font-semibold text-slate-800 dark:text-slate-200 uppercase tracking-wide truncate">
                  {user}
                </span>
                {user !== 'admin' && toko && (
                  <span className="text-xs text-amber-600 dark:text-amber-400 font-medium uppercase truncate">
                    {toko}
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={() => setIsOpenModalLogout(true)}
              className="w-full py-2 px-3 rounded-lg flex justify-center items-center gap-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-700 dark:hover:text-red-300 font-medium text-sm transition-colors border border-transparent hover:border-red-100 dark:hover:border-red-800/40"
            >
              <IconLogout size={18} />
              <span>Log Out</span>
            </button>
          </div>
        </div>

      </div>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background-color: #f1f5f9;
          border-radius: 20px;
        }
        .custom-scrollbar:hover::-webkit-scrollbar-thumb {
          background-color: #cbd5e1;
        }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb {
          background-color: #1e293b;
        }
        .dark .custom-scrollbar:hover::-webkit-scrollbar-thumb {
          background-color: #334155;
        }
      `}</style>
    </div>
  );
}

export default Sidebar;
